import { useState, type FormEvent } from 'react';
import { t } from '../i18n';
import { AuthFailure, type ConsoleApi } from '../lib/api';
import { Button, Card, Field, FormFooter, Notice, PageHeader } from '../ui';
import type { TeamSession } from './auth-gate';
import { checkPassword, passwordChecks, type PasswordError } from './password';
import { CheckList, ChecksSummary, PasswordInput, type Check } from './password-field';

type State =
  | { readonly step: 'form'; readonly error: PasswordError | null }
  | { readonly step: 'saving' }
  | { readonly step: 'saved' };

/** "Tu cuenta": where a team member creates or changes the password. */
export function AccountScreen({
  api,
  session,
  recovery = false,
}: {
  api: ConsoleApi;
  session: TeamSession;
  /** True right after a "create your password" link: says why the person is here. */
  recovery?: boolean;
}) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [state, setState] = useState<State>({ step: 'form', error: null });
  // Which password fields show their text; every submit hides them again.
  const [visible, setVisible] = useState({ password: false, confirmation: false });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setVisible({ password: false, confirmation: false });
    const problem = checkPassword(password, confirmation, session.email);
    if (problem) {
      setState({ step: 'form', error: problem });
      return;
    }
    setState({ step: 'saving' });
    try {
      await api.updatePassword(password);
      setPassword('');
      setConfirmation('');
      setState({ step: 'saved' });
    } catch (error) {
      const reason = error instanceof AuthFailure ? error.reason : 'failed';
      setState({
        step: 'form',
        error: reason === 'invalid_credentials' ? 'failed' : reason,
      });
    }
  }

  const error = state.step === 'form' ? state.error : null;
  const errorId = error ? 'password-error' : null;
  const edit = (update: (value: string) => void) => (value: string) => {
    update(value);
    if (state.step === 'saved') setState({ step: 'form', error: null });
  };
  const checks = passwordChecks(password, confirmation, session.email);
  const newChecks: Check[] = [
    {
      key: 'length',
      state: checks.length,
      label: t(
        checks.tooLong ? 'account.password.checks.tooLong' : 'account.password.checks.length',
      ),
    },
    { key: 'guessable', state: checks.guessable, label: t('account.password.checks.guessable') },
  ];
  const matchChecks: Check[] = [
    { key: 'match', state: checks.match, label: t('account.password.checks.match') },
  ];
  const describedBy = (...ids: (string | null)[]) => ids.filter(Boolean).join(' ');

  return (
    <>
      <PageHeader
        title={t('account.title')}
        subtitle={t('account.subtitle', { email: session.email, role: t(`role.${session.role}`) })}
      />
      <Card title={t('account.password.title')} className="max-w-xl">
        <p id="password-help" className="text-[14.5px] text-muted">
          {t(recovery ? 'account.password.recovery' : 'account.password.help')}
        </p>
        <form className="grid gap-5" onSubmit={(event) => void submit(event)} noValidate>
          {/* Lets password managers save the new password under the right account. */}
          <input type="email" autoComplete="username" value={session.email} readOnly hidden />
          <Field
            label={t('account.password.new')}
            htmlFor="password-new"
            hint={<CheckList checks={newChecks} />}
            hintId="password-checks"
          >
            <PasswordInput
              id="password-new"
              autoComplete="new-password"
              value={password}
              onChange={edit(setPassword)}
              visible={visible.password}
              onVisibleChange={(shown) =>
                setVisible((current) => ({ ...current, password: shown }))
              }
              invalid={error !== null && error !== 'mismatch'}
              describedBy={describedBy('password-help', 'password-checks', errorId)}
            />
          </Field>
          <Field
            label={t('account.password.confirm')}
            htmlFor="password-confirm"
            hint={<CheckList checks={matchChecks} />}
            hintId="password-match"
          >
            <PasswordInput
              id="password-confirm"
              autoComplete="new-password"
              value={confirmation}
              onChange={edit(setConfirmation)}
              visible={visible.confirmation}
              onVisibleChange={(shown) =>
                setVisible((current) => ({ ...current, confirmation: shown }))
              }
              invalid={error === 'mismatch'}
              describedBy={describedBy('password-match', errorId)}
            />
          </Field>
          <ChecksSummary
            checks={[...newChecks, ...matchChecks]}
            active={password.length > 0 || confirmation.length > 0}
          />
          <FormFooter
            status={
              <>
                {error ? (
                  <Notice id="password-error">{t(`account.password.error.${error}`)}</Notice>
                ) : null}
              </>
            }
            live={state.step === 'saved' ? t('account.password.saved') : ''}
          >
            <Button type="submit" variant="primary" disabled={state.step === 'saving'}>
              {state.step === 'saving' ? t('account.password.saving') : t('account.password.save')}
            </Button>
          </FormFooter>
        </form>
      </Card>
    </>
  );
}
